import type { ReactElement } from "react";
import { useLocation, useParams } from "react-router";
import ErrorBoundary from "../components/app/ErrorBoundary";
import PermissionBoundary from "./PermissionBoundary";
import type { AppRoute, OwnedRouteProps } from "./routeManifest";

interface RouterRouteElementProps {
  route: AppRoute;
}

/** Supplies the router-neutral route contract retained from the Router 5 checkpoint. */
export default function RouterRouteElement({ route }: RouterRouteElementProps): ReactElement {
  const location = useLocation();
  const params = useParams<Record<string, string>>();
  const Component = route.component;
  const ownedProps: OwnedRouteProps = {
    location,
    // Router 6 adds a synthetic "*" param for the catch-all route. Router 5
    // supplied an empty object, so keep that application-visible contract.
    match: { params: route.path ? params : {} },
  };
  const element = (
    <ErrorBoundary key={location.pathname}>
      <Component {...ownedProps} />
    </ErrorBoundary>
  );

  return route.access === "authenticated" ? (
    <PermissionBoundary permission={route.permission}>{element}</PermissionBoundary>
  ) : (
    element
  );
}
